// src/AppBridge.js
// Uses CDN-hosted App Bridge (loaded via <script> tag in index.html)
// The new CDN auto-initializes via the <meta name="shopify-api-key"> tag

let appBridgeInstance = null;
let initializationAttempted = false;

// Check if we should actually use App Bridge
// This respects the decision made in index.html
export function shouldUseAppBridge() {
  // First check the flag set by index.html
  if (window.__SHOULD_LOAD_APP_BRIDGE__ === false) {
    return false;
  }
  
  // Then check URL params - only need host (shop is optional in new API)
  const params = new URLSearchParams(window.location.search);
  return params.has('host');
}

export function waitForShopify(maxAttempts = 50, delayMs = 100) {
  return new Promise((resolve) => {
    // If we shouldn't use App Bridge, resolve immediately with false
    if (!shouldUseAppBridge()) {
      resolve(false);
      return;
    }
    
    let attempts = 0;
    
    const check = () => {
      attempts++;
      // New CDN creates window.shopify directly with idToken method
      if (window.shopify && typeof window.shopify.idToken === 'function') {
        resolve(true);
      } else if (attempts >= maxAttempts) {
        //console.warn('[AppBridge] Timed out waiting for shopify CDN');
        resolve(false);
      } else {
        setTimeout(check, delayMs);
      }
    };
    
    check();
  });
}

export default function initAppBridge() {
  if (initializationAttempted) {
    return appBridgeInstance;
  }
  
  initializationAttempted = true;

  // Check if we should use App Bridge
  if (!shouldUseAppBridge()) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const host = params.get('host');
  
  if (!host) {
    return null;
  }

  try {
    // NEW CDN: window.shopify IS the app instance (auto-initialized via meta tag)
    if (!window.shopify) {
      return null;
    }

    // The new CDN auto-initializes - window.shopify IS the app
    appBridgeInstance = window.shopify;
    window.app = appBridgeInstance;
    
    return appBridgeInstance;
  } catch (error) {
    //console.error('[AppBridge] Init failed:', error.message);
    return null;
  }
}

export function getAppBridge() {
  // Return existing instance if we have it
  if (appBridgeInstance) {
    return appBridgeInstance;
  }
  
  // Try to get window.shopify directly (new CDN)
  if (window.shopify && typeof window.shopify.idToken === 'function') {
    appBridgeInstance = window.shopify;
    window.app = appBridgeInstance;
    return appBridgeInstance;
  }
  
  // Try to initialize
  if (shouldUseAppBridge()) {
    initAppBridge();
  }
  
  return appBridgeInstance || window.shopify || window.app;
}

// Kept for backward compatibility
export function isEmbeddedContext() {
  return shouldUseAppBridge();
}

// Get session token using the new API
export async function getSessionToken() {
  const app = getAppBridge();
  if (app && typeof app.idToken === 'function') {
    try {
      return await app.idToken();
    } catch (e) {
      console.error('[AppBridge] Failed to get idToken:', e);
    }
  }
  return null;
}

// Helper to wait for App Bridge to be ready
export function waitForAppBridge(maxAttempts = 10, delayMs = 100) {
  return new Promise((resolve, reject) => {
    if (!shouldUseAppBridge()) {
      reject(new Error('App Bridge not applicable in this context'));
      return;
    }
    
    let attempts = 0;
    
    const checkAppBridge = () => {
      attempts++;
      
      const app = getAppBridge();
      if (app && typeof app.idToken === 'function') {
        resolve(app);
        return;
      }
      
      if (attempts >= maxAttempts) {
        reject(new Error('App Bridge not available after maximum attempts'));
        return;
      }
      
      setTimeout(checkAppBridge, delayMs);
    };
    
    checkAppBridge();
  });
}